import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { ThemeContext } from '../contexts/ThemeContext';
import { authFetch } from '../services/authFetch';
import AdminSidebar from '../components/AdminSidebar';

const AdminLoginLogPage = () => {
  const [accordionState, setAccordionState] = useState({
    suratIjinOpen: false,
    laporanOpen: false,
    masterDataOpen: false
  });
  const [loginLogs, setLoginLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('all'); // all, student, teacher
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const { darkMode } = useContext(ThemeContext);
  const [user, setUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20); // Show 20 items per page

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    if (storedUser) {
      setUser(storedUser);
    }
  }, []);

  useEffect(() => {
    fetchLoginLogs();
  }, []);

  const fetchLoginLogs = async () => {
    try {
      setLoading(true);
      const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/').replace(/\/$/, '');
      const response = await authFetch(`${baseUrl}/api/admin/login-logs`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch login logs: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      if (result.success) {
        setLoginLogs(result.data);
      } else {
        throw new Error(result.message || 'Failed to fetch login logs');
      }
    } catch (err) {
      console.error('Error fetching login logs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/').replace(/\/$/, '');
  const avatarUrl = user?.avatar_url
    ? user.avatar_url.startsWith('http')
      ? user.avatar_url
      : `${baseUrl}${user.avatar_url.startsWith('/') ? '' : '/'}${user.avatar_url}`
    : '';

  const adminData = {
    name: user?.name || '',
    position: user?.role || '',
    avatarUrl
  };



  // Group logs by user and calculate statistics
  const userStats = loginLogs.reduce((acc, log) => {
    const key = `${log.user_id}-${log.user_type}`;
    if (!acc[key]) {
      acc[key] = {
        user_id: log.user_id,
        user_name: log.user_name,
        user_email: log.user_email,
        user_type: log.user_type,
        login_count: 0,
        last_login: null,
        first_login: null
      };
    }
    
    acc[key].login_count++;
    const loginTime = new Date(log.login_time);
    
    if (!acc[key].last_login || loginTime > new Date(acc[key].last_login)) {
      acc[key].last_login = log.login_time;
    }
    
    if (!acc[key].first_login || loginTime < new Date(acc[key].first_login)) {
      acc[key].first_login = log.login_time;
    }
    
    return acc;
  }, {});

  const sortedUserStats = Object.values(userStats)
    .filter(stat => {
      const matchesType = filterType === 'all' || stat.user_type === filterType;
      const matchesSearch = searchTerm === '' || 
        stat.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        stat.user_email.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Date filtering logic
      let matchesDate = true;
      if (dateFilter) {
        const filterDate = new Date(dateFilter);
        const lastLoginDate = new Date(stat.last_login);
        // Check if the last login date matches the selected date (same day)
        matchesDate = lastLoginDate.toDateString() === filterDate.toDateString();
      }
      
      return matchesType && matchesSearch && matchesDate;
    })
    .sort((a, b) => b.login_count - a.login_count);

  // Pagination logic
  const totalItems = sortedUserStats.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageItems = sortedUserStats.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterType, searchTerm, dateFilter]);

  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('id-ID', options);
  };

  const getUserTypeLabel = (type) => {
    switch(type) {
      case 'student': return 'Siswa';
      case 'teacher': return 'Guru';
      case 'admin': return 'Admin';
      default: return type;
    }
  };

  const getUserTypeBadgeColor = (type) => {
    switch(type) {
      case 'student': return '#3354a5';
      case 'teacher': return '#2ec27e';
      case 'admin': return '#e67700';
      default: return '#6c757d';
    }
  };

  return (
    <PageContainer>
      <PageHeader>
        <h1>Login Log</h1>
        <p>Monitor aktivitas login siswa dan guru</p>
      </PageHeader>

      <DashboardGrid>
        <MainSection>
          <FilterSection>
            <FilterGroup>
              <FilterLabel>Filter Tipe User:</FilterLabel>
              <FilterSelect 
                value={filterType} 
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">Semua</option>
                <option value="student">Siswa</option>
                <option value="teacher">Guru</option>
                <option value="admin">Admin</option>
              </FilterSelect>
            </FilterGroup>
            
            <FilterGroup>
              <FilterLabel>Cari User:</FilterLabel>
              <SearchInput
                type="text"
                placeholder="Cari nama atau email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </FilterGroup>
            
            <FilterGroup>
              <FilterLabel>Filter Tanggal:</FilterLabel>
              <DateInput
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
              />
            </FilterGroup>
          </FilterSection>

          {loading ? (
            <LoadingContainer>
              <LoadingSpinner />
              <p>Memuat data login log...</p>
            </LoadingContainer>
          ) : error ? (
            <ErrorContainer>
              <ErrorIcon>⚠️</ErrorIcon>
              <ErrorMessage>{error}</ErrorMessage>
              <RetryButton onClick={fetchLoginLogs}>Coba Lagi</RetryButton>
            </ErrorContainer>
          ) : (
            <>


              <SectionCard>
                <SectionHeader>
                  <h2>Statistik Login User</h2>
                  <p>Urutkan berdasarkan frekuensi login</p>
                </SectionHeader>
                
                <TableContainer>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHeaderCell>Nama</TableHeaderCell>
                        <TableHeaderCell>Email</TableHeaderCell>
                        <TableHeaderCell>Tipe</TableHeaderCell>
                        <TableHeaderCell>Jumlah Login</TableHeaderCell>
                        <TableHeaderCell>Login Terakhir</TableHeaderCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {currentPageItems.length > 0 ? (
                        currentPageItems.map((stat, index) => (
                          <TableRow key={`${stat.user_id}-${stat.user_type}`}>
                            <TableCell>
                              <UserInfo>
                                <UserRank>#{startIndex + index + 1}</UserRank>
                                <UserName>{stat.user_name}</UserName>
                              </UserInfo>
                            </TableCell>
                            <TableCell>{stat.user_email}</TableCell>
                            <TableCell>
                              <UserTypeBadge color={getUserTypeBadgeColor(stat.user_type)}>
                                {getUserTypeLabel(stat.user_type)}
                              </UserTypeBadge>
                            </TableCell>
                            <TableCell>
                              <LoginCount>{stat.login_count}x</LoginCount>
                            </TableCell>
                            <TableCell>{formatDate(stat.last_login)}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan="5">
                            <EmptyState>
                              <EmptyStateIcon>📊</EmptyStateIcon>
                              <EmptyStateText>Tidak ada data login log yang ditemukan</EmptyStateText>
                            </EmptyState>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <PaginationContainer>
                    <PaginationInfo>
                      Menampilkan {startIndex + 1}-{Math.min(endIndex, totalItems)} dari {totalItems} data
                    </PaginationInfo>
                    <PaginationControls>
                      <PaginationButton 
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                      >
                        ← Sebelumnya
                      </PaginationButton>
                      
                      <PageNumbers>
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <PageNumber
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              active={pageNum === currentPage}
                            >
                              {pageNum}
                            </PageNumber>
                          );
                        })}
                      </PageNumbers>
                      
                      <PaginationButton 
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                      >
                        Selanjutnya →
                      </PaginationButton>
                    </PaginationControls>
                  </PaginationContainer>
              </SectionCard>


            </>
          )}
        </MainSection>
        
        <AdminSidebar 
          user={adminData} 
          accordionState={accordionState} 
          setAccordionState={setAccordionState} 
        />
      </DashboardGrid>
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 1280px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 1.5rem;
  
  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
    margin-bottom: 0.5rem;
  }
  
  p {
    color: var(--text-light);
    font-size: 0.875rem;
  }
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 1.5rem;
  
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  order: 2;
  
  @media (max-width: 1100px) {
    order: 1;
  }
`;

const FilterSection = styled.div`
  background-color: var(--background);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: var(--box-shadow);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FilterLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-dark);
`;

const FilterSelect = styled.select`
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  background-color: var(--background);
  color: var(--text-dark);
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const SearchInput = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  background-color: var(--background);
  color: var(--text-dark);
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
  
  &::placeholder {
    color: var(--text-light);
  }
`;

const DateInput = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  background-color: var(--background);
  color: var(--text-dark);
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
  
  p {
    margin-top: 1rem;
    color: var(--text-light);
  }
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid var(--border);
  border-top: 3px solid var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
  background-color: var(--background);
  border-radius: 12px;
  box-shadow: var(--box-shadow);
`;

const ErrorIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const ErrorMessage = styled.p`
  color: var(--text-dark);
  margin-bottom: 1.5rem;
  font-size: 1rem;
`;

const RetryButton = styled.button`
  background-color: var(--primary);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: var(--primary-dark);
  }
`;



const SectionCard = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: var(--box-shadow);
`;

const SectionHeader = styled.div`
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--border);
  
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-dark);
    margin-bottom: 0.25rem;
  }
  
  p {
    font-size: 0.875rem;
    color: var(--text-light);
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: var(--background-light);
`;

const TableHeaderCell = styled.th`
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: var(--text-dark);
  font-size: 0.875rem;
  border-bottom: 1px solid var(--border);
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  &:hover {
    background-color: var(--background-light);
  }
`;

const TableCell = styled.td`
  padding: 1rem;
  border-bottom: 1px solid var(--border);
  font-size: 0.875rem;
  color: var(--text-dark);
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const UserRank = styled.div`
  background-color: var(--primary);
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  min-width: 30px;
  text-align: center;
`;

const UserName = styled.div`
  font-weight: 500;
`;

const UserTypeBadge = styled.span`
  background-color: ${props => props.color}20;
  color: ${props => props.color};
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  text-transform: uppercase;
`;

const LoginCount = styled.div`
  font-weight: 600;
  color: var(--primary);
`;



const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
`;

const EmptyStateIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
`;

const EmptyStateText = styled.p`
  color: var(--text-light);
  font-size: 1rem;
`;

const PaginationContainer = styled.div`
  padding: 1.5rem;
  border-top: 1px solid var(--border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const PaginationInfo = styled.div`
  color: var(--text-light);
  font-size: 0.875rem;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  background-color: var(--background);
  color: var(--text-dark);
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background-color: var(--primary);
    color: white;
    border-color: var(--primary);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageNumbers = styled.div`
  display: flex;
  gap: 0.25rem;
`;

const PageNumber = styled.button`
  width: 40px;
  height: 40px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background-color: ${props => props.active ? 'var(--primary)' : 'var(--background)'};
  color: ${props => props.active ? 'white' : 'var(--text-dark)'};
  font-size: 0.875rem;
  font-weight: ${props => props.active ? '600' : '400'};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.active ? 'var(--primary-dark)' : 'var(--primary)'};
    color: white;
    border-color: var(--primary);
  }
`;

export default AdminLoginLogPage;